import React, { useEffect, useMemo, useState } from "react";
import app from "../../firebase";
import { getAuth } from "firebase/auth";
import {
  getFirestore,
  collection,
  query,
  orderBy,
  limit,
  onSnapshot,
  startAfter,
  getDocs,
  updateDoc,
  doc,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";
import { Helmet } from "react-helmet-async";
import { motion, AnimatePresence } from "framer-motion";
import { FaSearch, FaExternalLinkAlt, FaCheck, FaTimes, FaFileAlt } from "react-icons/fa";
import { useToast } from "../../context/ToastContext";

const db = getFirestore(app);
const auth = getAuth(app);

const PAGE_SIZE = 25;

// normalize "new" to behave like "pending"
const normalizeStatus = (s) => (s || "new").toLowerCase();
const isPendingLike = (s) => {
  const n = normalizeStatus(s);
  return n === "pending" || n === "new";
};

export default function PartnerApplicationsPanel() {
  // Toasts
  const { addToast } = useToast() || {};
  const notify = (m, t = "info") => addToast?.(m, t);
  const success = (m) => notify(m, "success");
  const error = (m) => notify(m, "error");

  const [items, setItems] = useState([]);
  const [lastDoc, setLastDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filter, setFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("pending"); // pending | approved | declined | all
  const [preview, setPreview] = useState(null); // { url, title }
  const [confirm, setConfirm] = useState(null); // { id, action: 'approve'|'decline' }
  const [notes, setNotes] = useState({}); // per-row notes: { [id]: string }

  // initial load (realtime)
  useEffect(() => {
    setLoading(true);
    const base = collection(db, "partner_applications");
    const q = query(base, orderBy("createdAt", "desc"), limit(PAGE_SIZE));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const data = snap.docs.map((d) => ({ id: d.id, ...d.data(), _ref: d }));
        setItems(data);
        setLastDoc(snap.docs[snap.docs.length - 1] || null);
        setLoading(false);
      },
      () => {
        setLoading(false);
        error?.("Unable to load partner applications (rules or network).");
      }
    );
    return () => unsub();
  }, []); // eslint-disable-line

  const loadMore = async () => {
    if (!lastDoc) return;
    setLoadingMore(true);
    try {
      const base = collection(db, "partner_applications");
      const q = query(base, orderBy("createdAt", "desc"), startAfter(lastDoc), limit(PAGE_SIZE));
      const snap = await getDocs(q);
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data(), _ref: d }));
      setItems((prev) => [...prev, ...data]);
      setLastDoc(snap.docs[snap.docs.length - 1] || null);
    } catch {
      error?.("Couldn't load more.");
    } finally {
      setLoadingMore(false);
    }
  };

  const filtered = useMemo(() => {
    const f = filter.trim().toLowerCase();
    return items.filter((it) => {
      const s = normalizeStatus(it.status);
      // status filtering
      if (statusFilter !== "all") {
        if (statusFilter === "pending") {
          if (!isPendingLike(s)) return false; // include 'new' + 'pending'
        } else if (s !== statusFilter) {
          return false;
        }
      }
      if (!f) return true;
      const hay = `${it.fullName} ${it.email} ${it.phone} ${it.state} ${it.npn} ${Object.keys(
        it.lines || {}
      ).join(" ")}`.toLowerCase();
      return hay.includes(f);
    });
  }, [items, filter, statusFilter]);

  const setStatus = async (id, action) => {
    try {
      const user = auth.currentUser;
      if (!user) {
        error?.("You must be signed in as an admin.");
        return;
      }

      const ref = doc(db, "partner_applications", id);
      const newStatus = action === "approve" ? "approved" : "declined";
      const adminNote = notes[id] || "";

      // 1) Update Firestore
      await updateDoc(ref, {
        status: newStatus,
        reviewedAt: serverTimestamp(),
        reviewedBy: { uid: user.uid, email: user.email || null },
        adminNote,
      });

      // 2) Log activity
      await addDoc(collection(db, "admin_activity"), {
        type: "partner_application_review",
        action: newStatus,
        targetCollection: "partner_applications",
        targetId: id,
        note: adminNote,
        by: { uid: user.uid, email: user.email || null },
        createdAt: serverTimestamp(),
      });

      // 3) Email applicant via API (best-effort; non-fatal if it fails)
      try {
        const token = await user.getIdToken();
        const resp = await fetch("/api/partner-application-status-email", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ id }), // API will read the doc and email based on final saved data
        });

        if (!resp.ok) {
          // Don't fail the status change — just let the admin know email didn't go
          const { error: apiErr } = (await resp.json().catch(() => ({}))) || {};
          notify(
            `Status saved, but email not sent${
              apiErr ? `: ${apiErr}` : ""
            }.`,
            "error"
          );
        }
      } catch (mailErr) {
        console.warn("Email API call failed:", mailErr);
        notify("Status saved. Email could not be sent.", "error");
      }

      success?.(
        `Application ${newStatus === "approved" ? "approved" : "declined"}`
      );
      setConfirm(null);
      setNotes((prev) => ({ ...prev, [id]: "" }));
    } catch (e) {
      console.error(e);
      error?.("Update failed. Try again.");
    }
  };

  return (
    <>
      <Helmet>
        <title>Admin • Partner Applications</title>
      </Helmet>

      {/* Controls */}
      <div className="flex flex-col md:flex-row md:items-center gap-3 mb-4">
        <div className="relative flex-1">
          <FaSearch className="absolute left-3 top-3.5 text-gray-400" />
          <input
            className="w-full pl-9 pr-3 py-2 rounded-xl border"
            placeholder="Search name, email, state, NPN…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>
        <select
          className="rounded-xl border px-3 py-2"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="declined">Declined</option>
          <option value="all">All</option>
        </select>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-2xl border bg-white">
        <table className="min-w-[900px] w-full text-left">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3">Applicant</th>
              <th className="px-4 py-3">State</th>
              <th className="px-4 py-3">Lines</th>
              <th className="px-4 py-3">Submitted</th>
              <th className="px-4 py-3">Files</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-gray-500">
                  Loading…
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-gray-500">
                  No applications.
                </td>
              </tr>
            ) : (
              filtered.map((it) => {
                const s = normalizeStatus(it.status);
                const badge =
                  s === "approved"
                    ? "bg-green-100 text-green-700"
                    : s === "declined"
                    ? "bg-red-100 text-red-700"
                    : "bg-yellow-100 text-yellow-700"; // pending/new

                return (
                  <tr key={it.id} className="border-t">
                    <td className="px-4 py-3">
                      <div className="font-semibold">{it.fullName}</div>
                      <div className="text-sm text-gray-600">{it.email}</div>
                      <div className="text-sm text-gray-600">{it.phone}</div>
                      <div className="text-xs text-gray-500">NPN: {it.npn || "—"}</div>
                    </td>
                    <td className="px-4 py-3">{it.state || "—"}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {Object.entries(it.lines || {})
                          .filter(([, v]) => v)
                          .map(([k]) => (
                            <span key={k} className="text-xs bg-gray-100 px-2 py-0.5 rounded-full">
                              {k}
                            </span>
                          ))}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {it.createdAt?.toDate?.()
                        ? it.createdAt.toDate().toLocaleString()
                        : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        {it.licenseUrl && (
                          <button
                            className="inline-flex items-center gap-1 px-2 py-1 rounded border text-sm"
                            onClick={() =>
                              setPreview({ url: it.licenseUrl, title: `${it.fullName} • License` })
                            }
                          >
                            <FaFileAlt /> License
                          </button>
                        )}
                        {it.eoUrl && (
                          <button
                            className="inline-flex items-center gap-1 px-2 py-1 rounded border text-sm"
                            onClick={() =>
                              setPreview({ url: it.eoUrl, title: `${it.fullName} • E&O` })
                            }
                          >
                            <FaFileAlt /> E&O
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-semibold px-2 py-1 rounded-full ${badge}`}>
                        {s === "new" ? "NEW" : s.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-2">
                        <textarea
                          placeholder="Optional admin note"
                          value={notes[it.id] || ""}
                          onChange={(e) =>
                            setNotes((prev) => ({ ...prev, [it.id]: e.target.value }))
                          }
                          className="w-full rounded border p-2 text-sm"
                          rows={2}
                        />
                        <div className="flex gap-2">
                          <button
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded bg-[#0ea5e9] text-white text-sm hover:opacity-90"
                            onClick={() => setConfirm({ id: it.id, action: "approve" })}
                            title="Approve"
                          >
                            <FaCheck /> Approve
                          </button>
                          <button
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded bg-[#ef4444] text-white text-sm hover:opacity-90"
                            onClick={() => setConfirm({ id: it.id, action: "decline" })}
                            title="Decline"
                          >
                            <FaTimes /> Decline
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Load more */}
      <div className="mt-4 flex justify-center">
        {lastDoc && (
          <button
            onClick={loadMore}
            disabled={loadingMore}
            className="px-4 py-2 rounded-xl border bg-white hover:bg-gray-50"
          >
            {loadingMore ? "Loading…" : "Load more"}
          </button>
        )}
      </div>

      {/* File Preview Modal */}
      <AnimatePresence>
        {preview && (
          <motion.div
            className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setPreview(null)}
          >
            <motion.div
              className="w-full max-w-4xl bg-white rounded-2xl shadow-xl overflow-hidden"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 20, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-4 py-3 border-b">
                <div className="font-bold">{preview.title}</div>
                <a
                  href={preview.url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm inline-flex items-center gap-1"
                >
                  Open in new tab <FaExternalLinkAlt />
                </a>
              </div>
              <div className="p-4">
                {/* Try to render image, else iframe (pdf, etc.) */}
                {/\.(png|jpe?g|webp|gif)(\?|$)/i.test(preview.url) ? (
                  <img src={preview.url} alt="Preview" className="max-h-[70vh] w-full object-contain" />
                ) : (
                  <iframe title="file" src={preview.url} className="w-full h-[70vh]" />
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Confirm Modal */}
      <AnimatePresence>
        {confirm && (
          <motion.div
            className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setConfirm(null)}
          >
            <motion.div
              className="w-full max-w-md bg-white rounded-2xl shadow-xl overflow-hidden"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 20, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-5 py-4 border-b font-bold">
                {confirm.action === "approve" ? "Approve Application" : "Decline Application"}
              </div>
              <div className="px-5 py-4 text-gray-700">
                Are you sure you want to {confirm.action} this application?
              </div>
              <div className="px-5 py-4 flex justify-end gap-2 border-t">
                <button className="px-4 py-2 rounded-xl border" onClick={() => setConfirm(null)}>
                  Cancel
                </button>
                <button
                  className={`px-4 py-2 rounded-xl text-white ${
                    confirm.action === "approve" ? "bg-[#10b981]" : "bg-[#ef4444]"
                  }`}
                  onClick={() => setStatus(confirm.id, confirm.action)}
                >
                  Confirm
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
